// Grammar Units 6 Data - Unit 6 (Subject-Verb Agreement)
// Based on Ch06.jsx - Subject-Verb Agreement Learning
// Organized for Supabase migration and systematic data management

export const grammarUnits6Data = [
  {
    id: 6,
    title: { traditional: '主詞動詞一致性', simplified: '主词动词一致性', english: 'Subject-Verb Agreement' },
    subtitle: { traditional: '掌握主詞動詞一致性的規則', simplified: '掌握主词动词一致性的规则', english: 'Master the rules of subject-verb agreement' },
    description: { traditional: '學習英文主詞動詞一致性的基本原則和特殊情況，確保語法正確。', simplified: '学习英文主词动词一致性的基本原则和特殊情况，确保语法正确。', english: 'Learn basic principles and special cases of English subject-verb agreement to ensure grammatical correctness.' },
    icon: '🤝',
    color: 'from-teal-500 to-cyan-600',
    status: 'notStarted',
    difficulty: 'intermediate',
    estimatedHours: 2,
    content: {
      overview: {
        title: "主詞動詞一致性 (Subject-Verb Agreement)",
        concept: "The verb must agree with the subject in number (singular/plural)",
        whenToUse: "Always when constructing sentences",
        howToUse: "Match singular subjects with singular verbs, plural subjects with plural verbs",
        memoryTip: "Remember: Singular subjects take singular verbs (adds -s), plural subjects take base form verbs"
      },
      basicRules: {
        title: "1. 基本規則 Basic Rules",
        rules: [
          {
            rule: "Singular Subject + Singular Verb",
            examples: [
              { en: "The old man sits on that bench every afternoon.", zh: "這個老人每天下午都坐在那個長板凳上。" },
              { en: "He has taken this class before.", zh: "他之前已經修過這門課。" }
            ]
          },
          {
            rule: "Plural Subject + Plural Verb", 
            examples: [
              { en: "We are happy.", zh: "我們很快樂。" },
              { en: "They eat dinner together.", zh: "他們一起吃晚餐。" }
            ]
          }
        ]
      },
      collectiveNouns: {
        title: "2. 集合名詞 Collective Nouns",
        concept: "Collective nouns can take singular or plural verbs depending on context",
        whenToUse: "When the subject is a group noun like family, class, team, etc.",
        howToUse: "Use singular verb when group acts as one unit, plural when members act individually",
        memoryTip: "Think: Is the group acting as ONE or as INDIVIDUALS?",
        rules: [
          {
            rule: "Collective Noun as Group (Singular Verb)",
            examples: [
              { en: "My family is big.", zh: "我的家庭很大。" },
              { en: "The team is winning.", zh: "這個團隊正在獲勝。" }
            ]
          },
          {
            rule: "Collective Noun as Individuals (Plural Verb)",
            examples: [
              { en: "My family are discussing the trip.", zh: "我的家人正在討論旅行。" },
              { en: "The team are wearing different uniforms.", zh: "隊員們穿著不同的制服。" }
            ]
          }
        ]
      },
      indefinitePronouns: {
        title: "3. 不定代名詞 Indefinite Pronouns",
        concept: "Most indefinite pronouns take singular verbs",
        whenToUse: "With pronouns like everyone, somebody, anyone, etc.",
        howToUse: "Use singular verb forms with most indefinite pronouns",
        memoryTip: "Words ending in -one, -body, -thing are usually singular!",
        rules: [
          {
            rule: "Singular Indefinite Pronouns",
            examples: [
              { en: "Everyone has his or her duty.", zh: "每個人都有自己的責任。" },
              { en: "Nobody agrees with the professor.", zh: "沒有人同意教授的看法。" }
            ]
          },
          {
            rule: "Note: Use 'their' in modern usage",
            examples: [
              { en: "Everyone should pursue their own dream.", zh: "每個人都應該追求自己的夢想。" }
            ]
          }
        ]
      },
      gerundsAndInfinitives: {
        title: "4. 動名詞與不定詞作主詞 Gerunds and Infinitives as Subjects",
        concept: "When gerunds, infinitives, or noun clauses act as subjects, use singular verbs",
        whenToUse: "When the subject is a verb form or clause acting as a noun",
        howToUse: "Always use singular verb form regardless of other nouns in the subject",
        memoryTip: "Think of the whole phrase as ONE THING = singular verb",
        rules: [
          {
            rule: "Gerund Phrases as Subject",
            examples: [
              { en: "Keeping early hours is a good habit for students.", zh: "早睡早起對學生來說是好習慣。" }
            ]
          },
          {
            rule: "Infinitive Phrases as Subject", 
            examples: [
              { en: "To love your country is to be willing to criticize it.", zh: "愛國就是願意對國事提出批評。" }
            ]
          }
        ]
      },
      compoundSubjects: {
        title: "5. 複合主詞 Compound Subjects",
        concept: "Compound subjects joined by 'and' usually take plural verbs",
        whenToUse: "When two or more subjects are joined by 'and'",
        howToUse: "Use plural verb unless the subjects refer to the same person or thing",
        memoryTip: "AND = plural, OR = look at the closer subject",
        rules: [
          {
            rule: "Compound Subjects with 'and' (Plural)",
            examples: [
              { en: "Tom and Jerry are good friends.", zh: "湯姆和傑瑞是好朋友。" },
              { en: "Bread and butter are my favorite foods.", zh: "麵包和奶油是我最愛的食物。" }
            ]
          },
          {
            rule: "Compound Subjects with 'or'/'nor' (Look at closer subject)",
            examples: [
              { en: "Neither the teacher nor the students are ready.", zh: "老師和學生都沒準備好。" },
              { en: "Either the cat or the dog is making that noise.", zh: "不是貓就是狗在發出那個聲音。" }
            ]
          }
        ]
      },
      specialCases: {
        title: "6. 特殊情況 Special Cases",
        rules: [
          {
            rule: "Fractions and Percentages",
            concept: "Fractions and percentages take singular or plural verbs based on the noun they refer to",
            examples: [
              { en: "Two-thirds of the students are absent.", zh: "三分之二的學生缺席。" },
              { en: "Fifty percent of the work is done.", zh: "百分之五十的工作完成了。" }
            ]
          },
          {
            rule: "Amounts and Measurements",
            concept: "Amounts and measurements usually take singular verbs",
            examples: [
              { en: "Ten dollars is not enough.", zh: "十塊錢不夠。" },
              { en: "Five miles is a long distance to walk.", zh: "五英里是很長的步行距離。" }
            ]
          },
          {
            rule: "Titles and Names",
            concept: "Titles of books, movies, etc. take singular verbs even if they contain plural words",
            examples: [
              { en: "The Three Musketeers is a classic novel.", zh: "《三劍客》是一部經典小說。" }
            ]
          }
        ]
      },
      practiceQuestions: [
        {
          question: "Choose the correct verb: 'The team ___ playing well today.'",
          options: ["is", "are", "was", "were"],
          correct: 0,
          explanation: "When 'team' acts as a single unit, use singular verb."
        },
        {
          question: "Complete: 'Everyone in the class ___ their homework.'",
          options: ["has", "have", "is", "are"],
          correct: 0,
          explanation: "'Everyone' is a singular indefinite pronoun, so use singular verb."
        },
        {
          question: "Which is correct? 'Neither the teacher nor the students ___ ready.'",
          options: ["is", "are", "was", "were"],
          correct: 1,
          explanation: "With 'or'/'nor', the verb agrees with the closer subject 'students'."
        },
        {
          question: "Choose: 'Reading books ___ my favorite hobby.'",
          options: ["is", "are", "was", "were"],
          correct: 0,
          explanation: "Gerund phrases as subjects take singular verbs."
        },
        {
          question: "Complete: 'Ten dollars ___ not enough for lunch.'",
          options: ["is", "are", "was", "were"],
          correct: 0,
          explanation: "Amounts of money take singular verbs."
        }
      ],
      memoryTips: {
        title: "記憶技巧 Memory Tips",
        tips: [
          {
            category: "基本規則",
            content: "單數主詞 + 單數動詞，複數主詞 + 複數動詞"
          },
          {
            category: "集合名詞",
            content: "團體當作一個單位 = 單數動詞，成員個別行動 = 複數動詞"
          },
          {
            category: "不定代名詞",
            content: "-one, -body, -thing 結尾的詞通常是單數"
          },
          {
            category: "複合主詞",
            content: "and = 複數，or/nor = 看較近的主詞"
          },
          {
            category: "動名詞/不定詞",
            content: "當主詞時，整個片語當作一件事 = 單數動詞"
          }
        ]
      }
    }
  }
];

// Export individual units for specific access
export const getUnitById = (unitId) => {
  return grammarUnits6Data.find(unit => unit.id === unitId);
};

export const getUnitsByDifficulty = (difficulty) => {
  return grammarUnits6Data.filter(unit => unit.difficulty === difficulty);
};

export const getCompletedUnits = () => {
  return grammarUnits6Data.filter(unit => unit.status === 'completed');
};

// Default export
export default grammarUnits6Data;
